import { getActiveApiUrl } from "constants/API_ENV";
import ENTITY_TYPE from "enums/ENTITY_TYPE";
import { METABASE_APP } from "enums/METABASE_APP";
import { METABASE_APP_TYPE } from "enums/METABASE_APP_TYPE";
import { ExcludableProfileType } from "features/PanelManagement/components/Filters/FilterComponents/typeFilterConfig";
import RequestParams from "types/RequestParams";
import getQueryParamsFromMap from "utils/getQueryParamsFromMap";

const BASE_URL = getBaseUrl();

const version = "/v1";
const endpoints = {
  getMetabaseEmbed: (
    metabaseApp: METABASE_APP,
    metabaseAppType: METABASE_APP_TYPE,
    appTypeNumber: number,
    queryParams?: Record<string, string>,
  ) => {
    const queryString = new URLSearchParams(queryParams).toString();
    return `${BASE_URL}${version}/metabase/${metabaseApp}/${metabaseAppType}/${appTypeNumber}${queryString ? `?${queryString}` : ""}`;
  },
  /*=== Account Controller Provider ===*/
  /**
   * In used: Get List
   * @param {({ deleted: Boolean, page: Number,size: Number, sort: String })} params
   * @returns
   */
  getAccounts: (params: RequestParams = {}) => {
    const baseParams = {
      deleted: false,
      page: 0,
      size: 9999,
      sort: "name",
      ...params,
    };
    const stringParams = getQueryParamsFromMap(baseParams);
    return `${BASE_URL}${version}/accounts${
      stringParams !== "" ? `?${stringParams}` : ""
    }`;
  },
  getAccountById: (accountId: string) =>
    `${BASE_URL}${version}/account/${accountId}`,
  getAccountFeatures: () => `${BASE_URL}${version}/account-features`,
  getAccountProjects: (accountId: string, params: RequestParams = {}) => {
    const stringParams = getQueryParamsFromMap(params);
    return `${BASE_URL}${version}/accounts/${accountId}/projects${
      stringParams !== "" ? `?${stringParams}` : ""
    }`;
  },
  postCreateAccount: () => `${BASE_URL}${version}/account`,
  putUpdateAccount: (id: string) => `${BASE_URL}${version}/account/${id}`,
  /**
   * In used: Delete
   * @param {String} id take from activeId in react-router-dom
   * @returns
   */
  deleteAccount: (id) => `${BASE_URL}${version}/account/${id}`,

  /*=== Authentication Controller Provider ===*/
  /**
   * In used: Create and get JWT token
   * @returns
   */
  postAuth: () => `${BASE_URL}${version}/auth`,

  /**
   * Verify MFA code after challenge
   * @returns
   */
  postMfaVerify: () => `${BASE_URL}${version}/auth/mfa/verify`,

  /**
   * Resend MFA challenge code
   * @returns
   */
  postMfaResend: () => `${BASE_URL}${version}/auth/mfa/resend`,

  /**
   * In used: get to verify password token
   * @returns
   */
  getVerifyPasswordToken: (token: string) =>
    `${BASE_URL}${version}/user/verifyPasswordVerificationToken/${token}`,

  /**
   * In used: get to verify password token
   * @returns
   */
  putResetPassword: (token) =>
    `${BASE_URL}${version}/user/resetPassword/${token}`,
  forgotPassword: (email: string) =>
    `${BASE_URL}${version}/user/forgotPassword/${encodeURIComponent(email)}`,

  /*=== Clip Controller Provider ===*/
  postVideoClip: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/clip`,
  getClipByVideoId: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/clip`,
  getClipsBySourceId: (projectId: string, sourceId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${sourceId}/clips`,
  getVideoClips: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videoClips?videoId=${encodeURIComponent(videoId)}`,

  /*=== Email Controller Provider ===*/
  /**
   * Not In Used
   * @returns
   */
  getAllEmail: () => "undefined",
  /**
   * Not In Used
   * @returns
   */
  postCreateEmailSend: () => "undefined",
  /**
   * Not In Used
   * @returns
   */
  getEmailById: () => "undefined",

  /*=== Gift Controller Provider ===*/
  postCreateGift: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/gifts`,

  /*=== Highlight Controller Provider ===*/
  // NOTE: for the moment Highlights endpoint is used to create Tags
  getHighlights: (projectId, videoId) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/highlights`,
  postVideoHighlight: (projectId, videoId) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/highlights`,
  deleteVideoHighlight: (projectId, videoId, highlightId) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/highlights/${highlightId}`,
  putVideoHighlight: (projectId, videoId, highlightId) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/highlights/${highlightId}`,

  /*=== Industry Controller Provider ===*/
  getAllIndustry: (params = {}) => {
    const baseParams = {
      deleted: false,
      page: 0,
      size: 9999,
      sort: "createdOn",
      ...params,
    };
    const stringParams = getQueryParamsFromMap(baseParams);
    return `${BASE_URL}${version}/industries${
      stringParams !== "" ? `?${stringParams}` : ""
    }`;
  },
  postCreateIndustry: () => `${BASE_URL}${version}/industry`,
  putUpdateIndustry: (id: string) => `${BASE_URL}${version}/industry/${id}`,
  deleteIndustry: (id: string) => `${BASE_URL}${version}/industry/${id}`,

  /*=== Invitation Controller Provider ===*/
  /**
   * Not In used: Update By Id
   * @returns
   */
  getAllInvitationByPidForScript: () => "undefined",
  /**
   * Not In used: Update By Id
   * @returns
   */
  getAllInvitationByPidForParticipant: () => "undefined",
  /**
   * Not In used: Update By Id
   * @returns
   */
  getAllInvitationByPidForProject: () => "undefined",

  /*=== Mobile User Controller Provider ===*/
  /**
   * Not In used: Update By Id
   * @returns
   */
  getAllMobileUserScriptsForParticipant: () => "undefined",

  /*=== Participant Controller Provider ===*/
  /**
   * In used: Get List
   * @param {String} projectId
   * @param {({ deleted: Boolean, page: Number,size: Number, sort: String })} params
   * @returns
   */
  getAllParticipants: (projectId, params = {}) => {
    const baseParams = {
      deleted: false,
      page: 0,
      size: 9999,
      sort: "createdOn",
      ...params,
    };
    const stringParams = getQueryParamsFromMap(baseParams);
    return `${BASE_URL}${version}/projects/${projectId}/participants${
      stringParams !== "" ? `?${stringParams}` : ""
    }`;
  },
  getParticipantNotes: (projectId: string, participantId: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/participants/${participantId}/notes`;
  },
  getParticipantById: (projectId, id) => {
    if (!id) {
      return "";
    }
    return `${BASE_URL}${version}/projects/${projectId}/participant/${id}`;
  },
  getParticipantByEmail: (email: string | undefined | null) => {
    if (!email) return "";
    return `${BASE_URL}${version}/participant?email=${encodeURIComponent(email)}`;
  },
  getParticipantByVideo: (projectId: string, videoId: string) => {
    if (!videoId) return "";
    return `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/participants`;
  },
  postParticipantNote: (projectId: string, participantId: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/participants/${participantId}/note`;
  },
  postNonProjectParticipant: () => `${BASE_URL}${version}/participant`,
  postCreateParticipant: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/participant`,
  postCreateParticipants: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/participants`,
  getParticipantEmails: (searchable: boolean) => {
    return `${BASE_URL}${version}/participants/emails?searchable=${searchable}`;
  },
  /**
   * In used: Update By Id
   * @param {String} projectId from Store
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  putUpdateParticipant: (projectId, id) =>
    `${BASE_URL}${version}/projects/${projectId}/participant/${id}`,
  putUpdateParticipantById: (id) => `${BASE_URL}${version}/participant/${id}`,
  /**
   * In used: Delete
   * @param {String} projectId from Store
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  /**
   * In used: Delete
   * @param {String} projectId from Store
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  deleteParticipant: (projectId, id) =>
    `${BASE_URL}${version}/projects/${projectId}/participant/${id}`,

  sendParticipantPin: () => `${BASE_URL}${version}/participant/sendPin`,

  /**
   * No In Used: It should be on Video, why here?
   * @returns
   */
  getAllParticipantWithVideosForProject: () => "undefined",

  /*=== Playlist Controller Provider ===*/
  getPlaylists: (projectId: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/playlists?sort=createdOn,desc`;
  },
  getPlaylistOrder: (projectId, playlistId) =>
    playlistId
      ? `${BASE_URL}${version}/projects/${projectId}/playlist/${playlistId}`
      : "",
  duplicatePlaylist: (projectId, playlistId) =>
    `${BASE_URL}${version}/projects/${projectId}/playlist/${playlistId}/duplicate`,
  getPlaceholder: () => "https://djdatb70kb26r.cloudfront.net/placeholder.png",
  postCreatePlaylist: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/playlists`,
  deletePlaylist: (projectId, id) =>
    `${BASE_URL}${version}/projects/${projectId}/playlist/${id}`,
  putUpdatePlaylist: (projectId, playlistId) =>
    playlistId
      ? `${BASE_URL}${version}/projects/${projectId}/playlist/${playlistId}`
      : "",
  postPlaylistTitle: (projectId, playlistId) =>
    playlistId
      ? `${BASE_URL}${version}/projects/${projectId}/playlist/${playlistId}/title`
      : "",
  /*=== Project Controller Provider ===*/
  /**
   * In used: Get List
   * @param {({ deleted: Boolean, page: Number,size: Number, sort: String })} params
   * @returns
   */
  getAllProjects: (params = {}) => {
    const baseParams = {
      deleted: false,
      page: 0,
      size: 9999,
      sort: "createdOn,desc",
      ...params,
    };
    const stringParams = getQueryParamsFromMap(baseParams);
    return `${BASE_URL}${version}/projects${
      stringParams !== "" ? `?${stringParams}` : ""
    }`;
  },
  /**
   * In used: Create New
   * @returns String
   */
  postCreateProject: () => `${BASE_URL}${version}/project`,
  /**
   * In used: Update
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  putUpdateProject: (id) => `${BASE_URL}${version}/project/${id}`,
  /**
   * In Used: get By Id
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  getProjectById: (id) => `${BASE_URL}${version}/project/${id}`,
  getProjectBySlug: (slugId: string) =>
    `${BASE_URL}${version}/project/bySlug/${encodeURIComponent(slugId)}`,
  getProjectFeatures: (projectId: string) =>
    `${BASE_URL}${version}/project/${projectId}/features`,
  /**
   * In used: Delete
   * @param {String} id activeId from react-router-dom
   * @returns
   */
  deleteProject: (id) => `${BASE_URL}${version}/project/${id}`,

  getUnicornProjects: () => `${BASE_URL}${version}/unicorn/projects`,
  postUnicornProjects: () => `${BASE_URL}${version}/unicorn/projects`,

  /*=== Question Controller Provider ===*/
  getQuestionById: (projectId: string, scriptId: string, questionId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/scripts/${scriptId}/questions/${questionId}`,

  getAnswerByVideoId: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/answer`,

  getAllQuestions: (projectId: string, params: string) =>
    `${BASE_URL}${version}/projects/${projectId}/questions${
      params ? `?${params}` : ""
    }`,

  getQuestionsByScriptId: (
    projectId: string,
    scriptId: string,
    queryParams?: string,
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/scripts/${scriptId}/questions${
      queryParams ? `?${queryParams}` : ""
    }`,

  /*=== Script Controller Provider ===*/
  /**
   * In used: get List
   * @param {String} projectId get from store
   * @param {({ deleted: Boolean, page: Number,size: Number, sort: String })} params
   * @returns
   */
  getAllScript: (projectId, params = {}) => {
    const stringParams = getQueryParamsFromMap(params);
    return `${BASE_URL}${version}/projects/${projectId}/scripts${
      stringParams ? `?${stringParams}` : ""
    }`;
  },
  /**
   *
   * @param {String} projectId get from Store
   * @param {String} scriptId activeId from react-router-dom
   * @param {({ deleted: Boolean, includeQuestions:Boolean, page: Number,size: Number, sort: String })} params
   * @returns
   */
  getScriptById: (projectId, scriptId, queryParams?: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/scripts/${scriptId}${
      queryParams ? `?${queryParams}` : ""
    }
    `;
  },

  getScriptsByParticipant: (projectId, participantId) =>
    `${BASE_URL}${version}/projects/${projectId}/participant/${participantId}/scripts`,
  postAddScriptToParticipant: (projectId, scriptId, participantId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/${scriptId}?participantId=${participantId}`,
  postScriptInviteParticipants: (projectId, scriptId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/${scriptId}/invitation`,
  postCreateScript: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/bulk`,
  autoCreateScript: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/script`,
  putUpdateScript: (projectId, scriptId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/bulk/${scriptId}`,
  deleteScript: (projectId, scriptId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/${scriptId}`,
  deleteInvite: (projectId, scriptId, participantId) =>
    `${BASE_URL}${version}/projects/${projectId}/script/${scriptId}/invite?participantId=${participantId}`,

  /*=== Search Controller Provider ===*/
  searchTranscripts: (q: string) =>
    `${BASE_URL}${version}/search/transcripts?q=${encodeURIComponent(q)}`,

  /*=== Utils — transcript embeddings ===*/
  embedTranscriptVideo: (videoId: string) =>
    `${BASE_URL}${version}/utils/transcripts/embeddings/videos/${videoId}`,
  getProjectTranscriptEmbedStatuses: (projectId: string) =>
    `${BASE_URL}${version}/utils/transcripts/embeddings/projects/${projectId}`,

  /*=== Session Controller Provider ===*/

  getSession: () => `${BASE_URL}${version}/session`,

  getConfig: () => `${BASE_URL}${version}/config`,
  getConfigByProjectId: (projectId: string) =>
    `${BASE_URL}${version}/config/${projectId}`,

  /*=== Share Controller Provider ===*/

  getShare: (projectId, shareId) =>
    `${BASE_URL}${version}/projects/${projectId}/shares/${shareId}`,
  putUpdateShare: (projectId, shareId) =>
    `${BASE_URL}${version}/projects/${projectId}/shares/${shareId}`,
  deleteShare: (projectId, shareId) =>
    `${BASE_URL}${version}/projects/${projectId}/shares/${shareId}`,
  postCreateShare: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/shares`,
  getAllShares: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/shares`,
  getShareFromToken: (token) => `${BASE_URL}${version}/share?token=${token}`,

  /*=== Tag Controller Provider ===*/
  getAllTags: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/tags?size=9999&sort=createdOn,desc`,
  postCreateTag: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/tags`,

  /*=== User Controller Provider ===*/

  getAllUsers: (projectId: string, params?: RequestParams) => {
    const stringParams = getQueryParamsFromMap(params);
    return `${BASE_URL}${version}/projects/${projectId}/users?${
      stringParams !== "" ? `${stringParams}` : ""
    }`;
  },

  deleteUser: (projectId, userId) =>
    `${BASE_URL}${version}/projects/${projectId}/user/${userId}`,

  getUserByPidAndId: (projectId, id) =>
    `${BASE_URL}${version}/projects/${projectId}/user/${id}`,
  postCreateUser: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/user`,
  putUpdateUser: (projectId, id) =>
    `${BASE_URL}${version}/projects/${projectId}/user/${id}`,
  patchUserTwoFactorAuth: (userId: string) =>
    `${BASE_URL}${version}/users/${userId}/two-factor-auth`,

  /*=== Timeline ===*/
  getProjectTimelines: (projectId: string) =>
    !projectId ? "" : `${BASE_URL}${version}/projects/${projectId}/timelines`,
  getTimelineByPlaylistId: (projectId: string, playlistId: string) =>
    !projectId
      ? ""
      : `${BASE_URL}${version}/projects/${projectId}/playlists/${playlistId}/timeline`,
  postTimeline: (projectId) =>
    `${BASE_URL}${version}/projects/${projectId}/timelines`,
  putTimelineByPlaylistId: (projectId: string, playlistId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/playlists/${playlistId}/timeline`,

  /*=== Video Controller Provider ===*/
  concatenateVideos: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/concatenate`,
  getVideo: (projectId, videoId: string | undefined) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}`
      : "",
  deleteVideo: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}`,
  postCreateVideo: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos`,
  postDuplicateVideo: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/duplicate`,
  getUploadUrl: (projectId, filename) =>
    `${BASE_URL}${version}/projects/${projectId}/sign/upload?s3ObjectKey=${filename}`,
  getSignedTranscodeUrl: (projectId, s3key?: string) =>
    s3key
      ? // GOTCHA: this endpoint has a trailing slash!
        `${BASE_URL}${version}/projects/${projectId}/sign/?s3ObjectKey=${s3key}`
      : "",
  getSignedUrlsForVideo: (projectId, videoId?: string) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/sign`
      : "",
  getVideos: (projectId: string, queryParams?: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/videos/search?${queryParams || ""}`;
  },
  getAllVideosByParticipant: (
    projectId,
    { scriptIds, tagIds }: { scriptIds: string[]; tagIds: string[] },
  ) => {
    const scriptIdsString =
      scriptIds.length > 0 ? `&scriptId=${scriptIds}` : "";
    const tagIdsString = tagIds.length > 0 ? `&tagIds=${tagIds}` : "";

    return `${BASE_URL}${version}/projects/${projectId}/videos/byParticipant?sort=firstName&${scriptIdsString}${tagIdsString}`;
  },
  getTranscription: (projectId, videoId?) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/transcription`
      : "",
  getTranscriptionRaw: (projectId, videoId?) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/transcription/raw`
      : "",

  getTranslation: (projectId, videoId?, languageCode?) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/translation?languageCode=${languageCode || ""}`
      : "",
  postSpeakerAlias: (projectId, videoId) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/speakerAlias`
      : "",
  postVideoParticipants: (projectId: string, videoId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/participants`,

  postVideoTranscribe: (projectId, videoId) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/transcription/${videoId}`
      : "",
  getVideoSubtitles: (projectId: string, videoId?: string) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/subtitles`
      : "",
  getVideoSubtitlesFile: (
    projectId: string,
    videoId?: string,
    languageCode?: string,
  ) => {
    if (!videoId) return "";
    const base = `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/subtitles/file`;
    if (!languageCode) return base;
    const params = new URLSearchParams({ languageCode });
    return `${base}?${params.toString()}`;
  },
  postVideoSubtitlesEnsure: (projectId: string, videoId?: string) =>
    videoId
      ? `${BASE_URL}${version}/projects/${projectId}/videos/${videoId}/subtitles/ensure`
      : "",

  /* S3 Signed Uploads */
  getNewSignedVideoId: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/sign/upload`,
  getVideoUploadParts: (
    projectId: string,
    videoId: string,
    numParts: number | string,
    extension = "mp4",
    contentType = "video/mp4",
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/sign/getMultipartPreSignedUrls/${videoId}?numParts=${numParts}&extension=${extension}&contentType=${encodeURIComponent(contentType)}`,
  finalizeVideoUpload: (
    projectId: string,
    videoId: string,
    extension?: string,
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/sign/finalizeMultipartUpload/${videoId}${extension ? `?extension=${extension}` : ""}`,
  getAccountLogoUploadParts: (
    accountId: string,
    numParts: number | string,
    extension = "png",
  ) =>
    `${BASE_URL}${version}/accounts/${accountId}/sign/getMultipartPreSignedUrls?numParts=${numParts}&extension=${extension}`,
  finalizeAccountLogoUpload: (accountId: string, extension = "png") =>
    `${BASE_URL}${version}/accounts/${accountId}/sign/finalizeMultipartUpload?extension=${extension}`,
  getSignedAccountLogoUrl: (accountId: string) =>
    `${BASE_URL}${version}/accounts/${accountId}/sign/logo`,
  deleteAccountLogo: (accountId: string) =>
    `${BASE_URL}${version}/accounts/${accountId}/sign/logo`,
  postTremendousConnectOnboarding: (accountId: string) =>
    `${BASE_URL}${version}/accounts/${accountId}/sign/tremendous/connect/onboarding`,

  /* Events */
  getEvents: (projectId: string, entityId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/events?entityId=${entityId}`,

  /* Segments */
  getSegmentParticipants: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/segmentParticipants`,
  putSegment: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/segments`,
  getSegments: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/segments`,
  deleteSegmentParticipant: (
    projectId: string,
    segmentId: string,
    participantId: string,
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/segments/${segmentId}/participants/${participantId}`,
  postSegmentParticipants: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/segmentParticipants`,

  /* Profile  */
  putOrPostProfileData: () => `${BASE_URL}${version}/profile/profileData`,
  putToggleProjectSearchability: (projectId: string) =>
    `${BASE_URL}${version}/profile/projects/${projectId}/toggleSearchability`,
  deleteProfileData: ({
    id,
    skipArchive = false,
  }: {
    id: string;
    skipArchive?: boolean;
  }) =>
    `${BASE_URL}${version}/profile/profileData/${id}?skipArchive=${skipArchive}`,
  putNoteText: (id: string) =>
    `${BASE_URL}${version}/profile/profileData/${id}`,
  postBasicProfile: (email: string, projectId: string) =>
    `${BASE_URL}${version}/profile/projects/${projectId}/basicProfile?email=${encodeURIComponent(email)}`,
  getProfileByEmail: (email: string) =>
    `${BASE_URL}${version}/profile?email=${encodeURIComponent(email)}`,
  getProfileMappings: () => `${BASE_URL}${version}/profile/mappings`,
  postProfileMappings: (question: string) =>
    `${BASE_URL}${version}/profile/mappings?question=${encodeURIComponent(question)}`,
  putProfileMappings: () => `${BASE_URL}${version}/profile/mappings`,
  getProfileData: ({
    showOnlySearchable = true,
    keywordSearch,
    excludedTypes,
  }: {
    keywordSearch?: string | undefined;
    showOnlySearchable?: boolean;
    excludedTypes?: ExcludableProfileType[] | undefined;
  } = {}) => {
    const excludedTypesString = excludedTypes?.length
      ? `&excludedTypes=${excludedTypes.join(",")}`
      : "";

    return `${BASE_URL}${version}/profile/search?showOnlySearchable=${showOnlySearchable}${keywordSearch ? `&keywordSearch=${encodeURIComponent(keywordSearch)}` : ""}${excludedTypesString}`;
  },
  getPaginatedProfileSearch: ({
    showOnlySearchable = true,
    keywordSearch,
    excludedTypes,
    hasContactNote,
    page = 0,
    size = 50,
    sort,
  }: {
    keywordSearch?: string | undefined;
    showOnlySearchable?: boolean;
    excludedTypes?: ExcludableProfileType[] | undefined;
    hasContactNote?: boolean;
    page?: number;
    size?: number;
    sort?: string | string[];
  } = {}) => {
    const params = new URLSearchParams();
    params.set("showOnlySearchable", String(showOnlySearchable));
    params.set("page", String(page));
    params.set("size", String(size));
    if (keywordSearch) {
      params.set("keywordSearch", keywordSearch);
    }
    if (excludedTypes?.length) {
      params.set("excludedTypes", excludedTypes.join(","));
    }
    if (hasContactNote !== undefined) {
      params.set("hasContactNote", String(hasContactNote));
    }
    const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];
    sortValues.forEach((value) => params.append("sort", value));

    return `${BASE_URL}${version}/profile/paginatedSearch?${params.toString()}`;
  },
  getProfileQuestions: () => `${BASE_URL}${version}/profile/question`,
  getProfileAnswers: () => `${BASE_URL}${version}/profile/answer`,
  getProjectRespondentSearchability: (projectid: string, email: string) =>
    `${BASE_URL}${version}/profile/getProjectRespondentSearchability?projectId=${encodeURIComponent(projectid)}&email=${encodeURIComponent(email)}`,
  setProjectRespondentSearchability: (
    projectid: string,
    email: string,
    searchable: boolean,
  ) =>
    `${BASE_URL}${version}/profile/setProjectRespondentSearchability?projectId=${encodeURIComponent(projectid)}&email=${encodeURIComponent(email)}&searchable=${searchable}`,

  /* Saved Filters */
  getSavedFilters: () => `${BASE_URL}${version}/savedFilters`,
  getNlpFilters: () => `${BASE_URL}${version}/nlpFilters`,
  postNlpFilters: () => `${BASE_URL}${version}/nlpFilters`,
  postNlpFiltersGenerate: () => `${BASE_URL}${version}/nlpFilters/generate`,
  putNlpFilters: (id: string) => `${BASE_URL}${version}/nlpFilters/${id}`,
  putSavedFilters: () => `${BASE_URL}${version}/savedFilters`,

  getSavedFiltersByProject: (projectId: string) =>
    `${BASE_URL}${version}/${projectId}/savedFilters`,
  putSavedFiltersByProject: (projectId: string) =>
    `${BASE_URL}${version}/${projectId}/savedFilters`,
  deleteSavedFilter: (filterId: string) =>
    `${BASE_URL}${version}/savedFilters/${filterId}`,

  /* Thumbnails */
  getThumbnailByEmail: (email: string) =>
    `${BASE_URL}${version}/thumbnails?email=${encodeURIComponent(email)}`,

  /* AI */
  deleteGenerativeAiRequestById: (projectId: string, entityId: string) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/generativeAiRequests/${entityId}`,
  retryAiRequestById: (projectId: string, responseId: string) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/retry/${responseId}`,
  getAiSummary: (
    projectId: string,
    entityType: ENTITY_TYPE,
    entityId: string,
  ) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/${entityType}/${entityId}/summary`,
  getProjectAiContent: (projectId: string) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/content`,
  getAiEntityById: (projectId: string, requestId: string) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/requests/${requestId}`,
  postVideosQuestion: (projectId: string) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/videos`,
  getAiSummaryByProject: (
    projectId: string,
    entityType: ENTITY_TYPE,
    entityId: string,
  ) =>
    `${BASE_URL}${version}/ai/projects/${projectId}/${entityType}/${entityId}/summary`,

  /* Persona sets (synthetic personas) */
  postPersonaSet: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets`,
  getPersonaSets: (projectId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets`,
  getPersonaSetById: (projectId: string, setId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}`,
  putPersonaSet: (projectId: string, setId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}`,
  deletePersonaSet: (projectId: string, setId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}`,
  putPersona: (projectId: string, setId: string, personaId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/personas/${personaId}`,
  deletePersona: (projectId: string, setId: string, personaId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/personas/${personaId}`,

  getPersonaSetConversations: (projectId: string, setId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/conversations`,
  getPersonaSetConversationById: (
    projectId: string,
    setId: string,
    conversationId: string,
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/conversations/${conversationId}`,
  postPersonaSetConversation: (projectId: string, setId: string) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/conversations`,
  postPersonaSetConversationMessage: (
    projectId: string,
    setId: string,
    conversationId: string,
  ) =>
    `${BASE_URL}${version}/projects/${projectId}/persona-sets/${setId}/conversations/${conversationId}/messages`,

  /* Notes */
  getEntityNotes: (
    projectId: string,
    entityType: ENTITY_TYPE,
    entityId: string,
  ) => {
    return `${BASE_URL}${version}/projects/${projectId}/${entityType}/${entityId}/notes`;
  },
  postEntityNote: (
    projectId: string,
    entityType: ENTITY_TYPE,
    entityId: string,
  ) => {
    return `${BASE_URL}${version}/projects/${projectId}/${entityType}/${entityId}/note`;
  },
  putEntityNote: (projectId: string, noteId: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/note/${noteId}`;
  },
  deleteEntityNote: (projectId: string, noteId: string) => {
    return `${BASE_URL}${version}/projects/${projectId}/note/${noteId}`;
  },

  postPublicParticipantSignup: () =>
    `${BASE_URL}${version}/public/participant-signup`,
};

export default endpoints;

function getBaseUrl(): string {
  let baseUrl = getActiveApiUrl();

  try {
    // @ts-expect-error Added to facilitate usage in Cypress tests
    Cypress.env("API_SERVER") && (baseUrl = Cypress.env("API_SERVER"));
  } catch (error) {
    // Ignore error if Cypress is not defined
  }

  return baseUrl;
}
